import { useEffect, useState } from 'react';

/* ----------------------------------------------------------------------------
 * useMediaQuery — subscribe to a CSS media query from React. Defaults to false
 * when matchMedia is unavailable (test/SSR). Kept dependency-free.
 * -------------------------------------------------------------------------- */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/* The shell's desktop breakpoint. Desktop layout (>= this width, Tailwind `lg`)
 * is byte-equivalent to the pre-responsive shell; below it collapses to drawers.
 * Keep in sync with the `lg:` prefixes used across the shell components. */
export const DESKTOP_MIN_WIDTH = 1024;

/** True on phone/tablet widths (< lg), where the shell uses drawers/sheets. */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${DESKTOP_MIN_WIDTH - 1}px)`);
}
